import React, { TouchEvent, useCallback, useEffect, useState } from 'react'
import { ROUTES } from '../../../lib/constants'
import styles from './Packages.module.scss'
import { Pill } from '../../../components/Pill/Pill'
import { SVGIcon } from '../../../components/SVGIcon/SVGIcon'
import { Button } from '../../../components/Button/Button'

export const Packages = (): React.JSX.Element => {
  const [showRight, setShowRight] = useState(true)
  const [showLeft, setShowLeft] = useState(false)
  const [position, setPosition] = useState(1)

  const onLeftClick = () => {
    setPosition(position - 1)
  }

  const onRightClick = () => {
    setPosition(position + 1)
  }

  const timeThreshhold = 100
  const swipeThreshhold = 50
  const [swipeStart, setSwipeStart] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const [swipeTimeBegin, setSwipeTimeBegin] = useState(0)

  const onTouchStart = (e: TouchEvent) => {
    if (window.innerWidth < 720) {
      setSwipeStart({
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      })
      setSwipeTimeBegin(e.timeStamp)
    }
  }

  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (window.innerWidth < 720) {
        const amount = swipeStart.x - e.changedTouches[0].clientX
        const time = e.timeStamp - swipeTimeBegin
        if (time > timeThreshhold && Math.abs(amount) >= swipeThreshhold) {
          setSwipeStart({ x: 0, y: 0 })
          setSwipeTimeBegin(0)
          if (amount > 0) {
            setPosition(position + 1)
          } else {
            setPosition(position - 1)
          }
        }
      }
    },
    [swipeStart, swipeTimeBegin]
  )

  useEffect(() => {
    switch (position) {
      case 1:
        setShowRight(true)
        setShowLeft(false)
        break

      case 2:
        setShowRight(true)
        setShowLeft(true)
        break

      case 3:
        setShowRight(false)
        setShowLeft(true)
        break

      default:
        if (position < 1) {
          setPosition(1)
        } else if (position > 3) {
          setPosition(3)
        }
        break
    }
  }, [position])

  return (
    <section className={styles.packages}>
      <h2 className="headline__h1 color--white">
        Which package is best for you?
      </h2>

      <div
        className={styles.container}
        data-position={position}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className={styles.grid}>
          {/* labels column */}
          <div
            className={styles.label}
            style={{ gridArea: 'price-label' }}
            data-desktop
          >
            Price Per Month
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'speed-label' }}
            data-desktop
          >
            Data Speed (Wired)
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'upload-label' }}
            data-desktop
          >
            Simultaneous
            <br />
            Upload and Download
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'coverage-label' }}
            data-desktop
          >
            Ideal WiFi Coverage For:
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'router-label' }}
            data-desktop
          >
            New WiFi Router Included ($500+ Value)
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'free-label' }}
            data-desktop
          >
            Free Standard Installation
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'protect-label' }}
            data-desktop
          >
            eero Secure
            <a
              href="/eeroCityside.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Learn More
            </a>
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'gaming-label' }}
            data-desktop
          >
            Ideal for gaming?
          </div>

          <div
            className={styles.label}
            style={{ gridArea: 'extenders-label' }}
            data-desktop
          >
            WiFi Extenders
          </div>

          {/* 1 Gig column */}
          <div
            className={styles.title}
            style={{ gridArea: 'title1' }}
            data-plan="1 Gig"
          >
            <h3 className="color--white">
              Connect &<br />
              Control
            </h3>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'price1' }}
            data-plan="1 Gig"
            data-first
          >
            <div className={styles.label} data-mobile>
              Price Per Month
            </div>
            <p className="headline__h3">$80</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'speed1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              Data Speed (Wired)
            </div>
            <p className="headline__h3">1 Gig</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'upload1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              Simultaneous
              <br />
              Upload and Download
            </div>
            <p className="headline__h3">1,000 MBPS</p>
            <p className="headline__h4">25x faster than traditional cable</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'coverage1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              Ideal WiFi Coverage For:
            </div>
            <p className="headline__h3">Homes and Apartments</p>
            <p className="headline__h4">up to 1,500 Sqft</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'router1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              New WiFi Router Included
              <br />
              ($500+ Value)
            </div>
            <p className="headline__h3">WiFi 7</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'free1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              Free Standard Installation
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--daylight-blue"
            />
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'protect1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              eero Secure
              <a
                href="/eeroCityside.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Learn More
              </a>
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--daylight-blue"
            />
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'gaming1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              Ideal for gaming?
            </div>
            <SVGIcon id="checkmark" color="transparent" />
            <p className="headline__h4"></p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'extenders1' }}
            data-plan="1 Gig"
          >
            <div className={styles.label} data-mobile>
              WiFi Extenders
            </div>
            <p className="headline__h3">Available for Rent</p>
          </div>

          <div
            className={styles.btnWrap}
            style={{ gridArea: 'btn1' }}
            data-plan="1 Gig"
          >
            <Button
              size="md"
              path={ROUTES.checkAvailability.path}
              className="bg-color--daylight-blue"
            >
              Select Package
            </Button>
          </div>

          {/* 2 Gigs column */}
          <div
            className={styles.title}
            style={{ gridArea: 'title2' }}
            data-plan="2 Gigs"
          >
            <Pill>Best Value!</Pill>
            <h3 className="color--white">
              Work Hard,
              <br />
              Play Hard
            </h3>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'price2' }}
            data-plan="2 Gigs"
            data-first
          >
            <div className={styles.label} data-mobile>
              Price Per Month
            </div>
            <p className="headline__h3">$95</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'speed2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              Data Speed (Wired)
            </div>
            <p className="headline__h3">2 Gigs</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'upload2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              Simultaneous
              <br />
              Upload and Download
            </div>
            <p className="headline__h3">2,000 MBPS</p>
            <p className="headline__h4">50x faster than traditional cable</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'coverage2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              Ideal WiFi Coverage For:
            </div>
            <p className="headline__h3">Homes and Apartments</p>
            <p className="headline__h4">up to 3,000 Sqft</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'router2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              New WiFi Router Included
              <br />
              ($500+ Value)
            </div>
            <p className="headline__h3">WiFi 7</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'free2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              Free Standard Installation
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--orange"
            />
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'protect2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              eero Secure
              <a
                href="/eeroCityside.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Learn More
              </a>
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--orange"
            />
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'gaming2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              Ideal for gaming?
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--orange"
            />
            <p className="headline__h4"></p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'extenders2' }}
            data-plan="2 Gigs"
          >
            <div className={styles.label} data-mobile>
              WiFi Extenders
            </div>
            <p className="headline__h3">1 Indoor Included</p>
          </div>

          <div
            className={styles.btnWrap}
            style={{ gridArea: 'btn2' }}
            data-plan="2 Gigs"
          >
            <Button
              size="md"
              path={ROUTES.checkAvailability.path}
              gradient="orange"
            >
              Select Package
            </Button>
          </div>

          {/* 5 Gigs column */}
          <div
            className={styles.title}
            style={{ gridArea: 'title3' }}
            data-plan="5 Gigs"
          >
            <h3 className="color--white">
              Everything &<br />
              Everywhere
            </h3>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'price3' }}
            data-plan="5 Gigs"
            data-first
          >
            <div className={styles.label} data-mobile>
              Price Per Month
            </div>
            <p className="headline__h3">$150</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'speed3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              Data Speed (Wired)
            </div>
            <p className="headline__h3">5 Gigs</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'upload3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              Simultaneous
              <br />
              Upload and Download
            </div>
            <p className="headline__h3">5,000 MBPS</p>
            <p className="headline__h4">125x faster than traditional cable</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'coverage3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              Ideal WiFi Coverage For:
            </div>
            <p className="headline__h3">Homes</p>
            <p className="headline__h4">
              up to 6,000 Sqft
              <br />+ 4000 Sqft Backyards
            </p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'router3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              New WiFi Router Included
              <br />
              ($500+ Value)
            </div>
            <p className="headline__h3">WiFi 7</p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'free3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              Free Standard Installation
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--ocean-blue"
            />
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'protect3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              eero Secure
              <a
                href="/eeroCityside.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Learn More
              </a>
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--ocean-blue"
            />
          </div>


          <div
            className={styles.feature}
            style={{ gridArea: 'gaming3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              Ideal for gaming?
            </div>
            <SVGIcon
              id="checkmark"
              color="white"
              className="bg-color--ocean-blue"
            />
            <p className="headline__h4"></p>
          </div>

          <div
            className={styles.feature}
            style={{ gridArea: 'extenders3' }}
            data-plan="5 Gigs"
          >
            <div className={styles.label} data-mobile>
              WiFi Extenders
            </div>
            <p className="headline__h3">
              1 Indoor &<br />1 Outdoor Included
            </p>
          </div>

          <div
            className={styles.btnWrap}
            style={{ gridArea: 'btn3' }}
            data-plan="5 Gigs"
          >
            <Button
              size="md"
              path={ROUTES.checkAvailability.path}
              className="bg-color--ocean-blue"
            >
              Select Package
            </Button>
          </div>
        </div>

        <div className={styles.mobileNav} data-show={showRight}>
          <button onClick={onRightClick}>
            <SVGIcon id="chevron" color="white" />
          </button>
        </div>

        <div className={styles.mobileNav} data-left data-show={showLeft}>
          <button onClick={onLeftClick}>
            <SVGIcon id="chevron" color="white" rotate={180} />
          </button>
        </div>
      </div>
    </section>
  )
}
